import React, { useEffect, useState, useContext } from 'react';
import {
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP,
  withErrorBoundary
} from '@nile/components';
import {
  Card,
  Heading,
  TextStyle,
  SkeletonBodyText,
  Button
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { GET_AI_FEATURE_METRIC_DATA } from '@nile/banyan/src/Graphql/Query/ai';
import { useCachedQuery, AccountClient } from '@nile/graphql';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { FormattedMessage } from 'react-intl';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';

const InfoCard = ({ heading, value, percent }) => {
  return (
    <div className="min-w200">
      <div className="mb10">
        <TextStyle>{heading}</TextStyle>
      </div>
      <div className="mb10">
        <Heading element="h2" weight="SemiBold">
          {value}
        </Heading>
      </div>
      <div className="fsize12 flex actr g5">
        <div>
          <TextStyle variation="subdued" weight="SemiBold">
            {percent || 0}%
          </TextStyle>
        </div>
        <div>
          <TextStyle variation="subdued">
            <FormattedMessage id="common.of.acceptanceRate" />
          </TextStyle>
        </div>
      </div>
    </div>
  );
};

const getPercentageFromValues = (total, accepted) => {
  return Math.round((accepted / total) * 100);
};

const TriageInsightsCard = () => {
  const triageAgentId = AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE;
  const tabContext = useContext(TabContext);

  const formatData = (data) => {
    const acceptanceRate = getPercentageFromValues(
      data?.ticketsProcessed,
      data?.ticketAccepted
    );
    const fieldClassificationRate = getPercentageFromValues(
      data?.fieldClassificationSuggested,
      data?.fieldClassificationAccepted
    );
    const clarificationsRate = getPercentageFromValues(
      data?.clarificationsSuggested,
      data?.clarificationsAccepted
    );
    const technicianGroupRate = getPercentageFromValues(
      data?.technicianGroupAssignmentsSuggested,
      data?.technicianGroupAccepted
    );
    return {
      ...data,
      acceptanceRate,
      fieldClassificationRate,
      clarificationsRate,
      technicianGroupRate
    };
  };

  const [triageAgentMetrics, setTriageAgentMetrics] = useState(null);

  const { loading, data } = useCachedQuery(GET_AI_FEATURE_METRIC_DATA, {
    client: AccountClient,
    variables: {
      input: {
        featureId: triageAgentId
      }
    }
  });

  useEffect(() => {
    if (data?.getAIFeatureMetricData?.data) {
      const triageAgentMetricsResult = formatData(
        data?.getAIFeatureMetricData?.data
      );
      setTriageAgentMetrics(triageAgentMetricsResult);
    }
  }, [data]);

  const openTriageAgent = () => {
    tabContext.openTab({
      module: 'AGENTMARKETPLACE',
      url: '/agent/marketplace/14/ticket-triage/ai-agent'
    });
  };

  return (
    <Card padding="sizeSmall">
      <div className="flex fx-sb">
        <div className="flex g10 actr">
          <div className="flex">
            <SVGIcon name="TriageAgent" />
          </div>
          <div>
            <Heading element="h3" weight="SemiBold">
              <FormattedMessage id="common.triage.agent" />
            </Heading>
          </div>
        </div>
        <div>
          <Button
            onClick={openTriageAgent}
            plain
            icon={<SVGIcon name="SettingsDark" />}
          >
            <FormattedMessage id="common.label.configure" />
          </Button>
        </div>
      </div>

      <div className="mt20 w100">
        {!triageAgentMetrics || (!triageAgentMetrics && loading) ? (
          <div className="flex g40 w100">
            <div className="w200">
              <SkeletonBodyText lines={3} />
            </div>
            <div className="w200">
              <SkeletonBodyText lines={3} />
            </div>
            <div className="w200">
              <SkeletonBodyText lines={3} />
            </div>
            <div className="w200">
              <SkeletonBodyText lines={3} />
            </div>
          </div>
        ) : (
          <div className="flex g40 wrap">
            <InfoCard
              heading="Total tickets triaged"
              value={triageAgentMetrics?.ticketsProcessed}
              percent={triageAgentMetrics?.acceptanceRate}
            />
            <InfoCard
              heading="Field Classification"
              value={triageAgentMetrics?.fieldClassificationSuggested}
              percent={triageAgentMetrics?.fieldClassificationRate}
            />
            <InfoCard
              heading="Clarifications"
              value={triageAgentMetrics?.clarificationsSuggested}
              percent={triageAgentMetrics?.clarificationsRate}
            />
            <InfoCard
              heading="Team Assignment"
              value={triageAgentMetrics?.technicianGroupAssignmentsSuggested}
              percent={triageAgentMetrics?.technicianGroupRate}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

const TriageInsightsCardWrapper = () => {
  const aiFeatureHook = AIFeaturesHook();

  const isAgentEnabled =
    aiFeatureHook?.getIsAIFeatureEnabled('TICKET_TRIAGE') || false;

  return isAgentEnabled ? <TriageInsightsCard /> : <></>;
};

export default withErrorBoundary(TriageInsightsCardWrapper);
